import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableBody,
  FormControlLabel,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import DeleteForeverTwoToneIcon from "@mui/icons-material/DeleteForeverTwoTone";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import EditableColumn from "../../../../../../../../../../components/EditableColumn";
import { FreeTextItemAnswer } from "app/containers/Questions/types";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "app/containers/Questions/redux/slice";
import { useTranslation } from "react-i18next";
import { selectDirection } from "styles/theme/slice";
import { useStyles } from "./styles";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: "14px",
    fontWeight: 400,
    border: 0,
    height: "45px",
    padding: "0 16px",
    width: "150px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "14px",
    fontWeight: 400,
    padding: "0 16px",
    height: "45px",
    width: "150px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#F3F6F8",
  },
  "& td, & th": {
    border: 0,
  },
}));

const FreeTextCard = ({
  disabledDelete,
  disabled,
  data,
  dataIndex,
  isPlotRTL,
}: {
  disabledDelete?: boolean;
  disabled?: boolean;
  dataIndex: number;
  isPlotRTL: boolean;
  data: FreeTextItemAnswer[];
}) => {
  const direction = useSelector(selectDirection)
  const inputRefsTop = useRef<(HTMLInputElement | null)[]>([]);
  const inputRefsBottom = useRef<(HTMLInputElement | null)[]>([]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
const styles= useStyles()
  const dir = isPlotRTL ? "rtl" : "ltr";
  const align = isPlotRTL ? "right" : "left";


  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null,
  ) => {
    if(newAlignment === 'left') {
      dispatch(
        actions.toggleUpdateFreeTextRtlOption({
          index: dataIndex,
          value: false,
        })
      );
    }
    if(newAlignment === 'right') {
      dispatch(
        actions.toggleUpdateFreeTextRtlOption({
          index: dataIndex,
          value: true,
        })
      );
    }
  
  };

  return (
    <Box
      sx={{
        border: "1px solid #207DB9",
        borderRadius: "4px",
        marginTop: "10px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          background: "#F3F6F8",
          borderRadius: "4px 4px 0px 0px",
          height: "36px",
          padding: "10px",
          position:'relative'
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "18px",
            letterSpacing: "0em",
            textAlign: "center",
            color: "#207DB9",
          }}
        >
          {`${t("QUESTIONS.FORM.LABELS.FREE_TEXT_TITLE", {
            num: dataIndex + 1,
          })}`}
        </Typography>
        <Box
          sx={{
            position: "absolute",
            right: 20,
            top: 2,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: direction == 'rtl' ? 'row-reverse' : 'row'
          }}
        >
          <ToggleButtonGroup
            value={align}
            exclusive
            size="small" 
            onChange={handleAlignment}
            dir="ltr"
            color="primary"
            sx={{
              '& .MuiToggleButton-root': {
                padding: '1px 3px', // Reduces padding
                fontSize: '0.55rem', // Decreases font size
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: 'primary.main', // Ensures primary color when selected
                color: 'white', // Ensures contrast
                '&:hover': {
                  backgroundColor: 'primary.dark', // Darker shade on hover
                },
              },
            }}
          >
            <Tooltip title={t('GAMES.FORM.LABELS.IS_LTR_PLOT')}>
            <ToggleButton size="small" value="left" aria-label="left aligned">
              <FormatAlignLeftIcon /><Typography sx={{marginLeft: '10px', marginRight: '10px'}}>  {t('LANGUAGES.EN')} </Typography>
            </ToggleButton>
            </Tooltip>
            <Tooltip title={t('GAMES.FORM.LABELS.IS_RTL_PLOT')}>
            <ToggleButton size="small" value="right" aria-label="right aligned">
              <FormatAlignRightIcon /><Typography sx={{marginLeft: '10px', marginRight: '10px'}}>  {t('LANGUAGES.HE')} </Typography>
            </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          

          <IconButton
            sx={{
              marginTop: '2px',
              width: "24px",
              height: "24px",
              borderRadius: "4px",
            }}
            disabled={disabledDelete}
            onClick={() => {
              dispatch(
                actions.setDeleteAnswer({
                  index: dataIndex,
                  type: "free_text",
                })
              );
            }}
          >
            <DeleteForeverTwoToneIcon
              sx={{
                fontSize: "16px",
                color: disabledDelete ? "#ccc" : "#E24399",
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Table dir={dir} sx={{ minWidth: "100%" }} aria-label="free table">
        <TableHead>
          <TableRow>
            <StyledTableCell>#</StyledTableCell>
            {data.map((row, index) => (
              <StyledTableCell align={align}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "16px",
                    letterSpacing: "0em",
                    color: "#5E6781",
                    padding: "8px",
                  }}
                >
                  {index + 1}
                </Typography>
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell sx={{}} component="th" scope="row">
              {t("QUESTIONS.FORM.LABELS.SHOW_LETTER")}
            </StyledTableCell>
            {data?.map((row, index) => (
              <StyledTableCell
                sx={{}}
                align={align}
                key={index}
                component="th"
                scope="row"
              >
                <Checkbox
                  checked={row.isVisible}
                  size="small"
                  disabled={disabled}
                  onChange={() => {
                    dispatch(
                      actions.setUpdateFreeTextAnswer({
                        index: dataIndex,
                        innerIndex: index,
                        key: "isVisible",
                        value: !row.isVisible,
                      })
                    );
                  }}
                  sx={{
                    margin: 0,
                    padding: "4px",
                    color: "#207DB9",
                    "&.Mui-checked": {
                      color: "#207DB9",
                    },
                  }}
                />
              </StyledTableCell>
            ))}
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell sx={{}} component="th" scope="row">
              {t("QUESTIONS.FORM.LABELS.OPTION_ONE")}
            </StyledTableCell>
            {data?.map((row, index) => (
              <StyledTableCell sx={{}} key={index} component="th" scope="row">
                <EditableColumn
                  ref={(el) => (inputRefsTop.current[index] = el)}
                  value={row.option1}
                  isOdd={index % 2 === 0}
                  disabled={disabled}
                  maxLength={1}
                  onKeyUp={(e) => {
                    if (e.key == "Backspace") {
                      inputRefsTop.current[index - 1]?.focus();
                    }
                  }}
                  onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                    event.target.select();
                  }} 
                  onSave={(val) => {
                    dispatch(
                      actions.setUpdateFreeTextAnswer({
                        index: dataIndex,
                        innerIndex: index,
                        key: "option1",
                        value: val,
                      })
                    );
                    if (val.length === 1) {
                      // Move to the next input
                      inputRefsTop.current[index + 1]?.focus();
                    }
                  }}
                />
              </StyledTableCell>
            ))}
          </StyledTableRow>

          <StyledTableRow>
            <StyledTableCell sx={{}} component="th" scope="row">
              {t("QUESTIONS.FORM.LABELS.OPTION_TWO")}
            </StyledTableCell>
            {data?.map((row, index) => (
              <StyledTableCell sx={{}} key={index} component="th" scope="row">
                <EditableColumn
                  ref={(el) => (inputRefsBottom.current[index] = el)}
                  value={!row.option1 ? "" : row.option2 }
                  isOdd={index % 2 === 0}
                  disabled={disabled || !row.option1}
                  maxLength={1}
                  onKeyUp={(e) => {
                    if (e.key == "Backspace") {
                      inputRefsBottom.current[index - 1]?.focus();
                    }
                  }}
                  onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                    event.target.select();
                  }} 
                  onSave={(val) => {
                    dispatch(
                      actions.setUpdateFreeTextAnswer({
                        index: dataIndex,
                        innerIndex: index,
                        key: "option2",
                        value: val,
                      })
                    );
                    if (val.length === 1) {
                      // Move to the next input
                      inputRefsBottom.current[index + 1]?.focus();
                    }
                  }}
                />
              </StyledTableCell>
            ))}
          </StyledTableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

export default FreeTextCard;
